// FILE: components/cards/ExamCard.tsx
"use client";

import { hardNavigate } from "@/components/shared/HardNavLink";
import { Star, Users, Clock, Gem, Play, BookOpen } from "lucide-react";
import UserHoverCard from "@/components/shared/UserHoverCard";

export interface ExamCardProps {
  title: string;
  author: string;
  authorId?: string;
  authorUsername?: string | null;
  authorAvatar?: string | null;
  isOwner?: boolean;
  viewCount?: number;
  rating: number;
  attempts: string;
  time: string;
  questions: number;
  tags: string[];
  price: number | "free";
  emoji: string;
  gradient?: string;
  org?: string;
  orgHref?: string;
  position?: string;
  positionHref?: string;
  isPurchased?: boolean;
  onClick?: () => void;
  onAuthorClick?: () => void;
}

export default function ExamCard({
  title, author, rating, attempts, time, questions,
  tags, price, emoji, gradient = "from-[#0f3a4a] to-[#1a5570]",
  org, orgHref, position, positionHref, isPurchased, onClick, onAuthorClick,
  authorAvatar, authorId, authorUsername, isOwner, viewCount,
}: ExamCardProps) {
  return (
    <div onClick={onClick}
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden cursor-pointer hover:shadow-md hover:-translate-y-1 active:scale-[0.98] active:translate-y-0 transition-all duration-200 group flex flex-col h-full">

      {/* Cover */}
      <div className={`relative h-36 bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,.12),transparent_60%)]" />
        <span className="text-5xl opacity-80 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,.2)] relative z-10">
          {emoji}
        </span>

{/* Owner badge */}
        {isOwner && (
          <div className="absolute top-2.5 left-2.5 z-10">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white shadow-sm">👑 Đề của bạn</span>
          </div>
        )}

        {/* Price badge */}
        <div className="absolute top-2.5 right-2.5">
          {price === "free" || price === 0 ? (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500 text-white shadow-sm">FREE</span>
          ) : (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500 text-white shadow-sm">
              <Gem className="w-3 h-3" />{price}
            </span>
          )}
        </div>

        {/* Question count */}
        <div className="absolute bottom-2.5 left-2.5">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-black/30 text-white/90 backdrop-blur-sm">
            <BookOpen className="w-3 h-3" />{questions} câu
          </span>
        </div>

        {/* Time badge */}
        <div className="absolute bottom-2.5 right-2.5">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-black/30 text-white/90 backdrop-blur-sm">
            <Clock className="w-3 h-3" />{time}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        {/* Org + Position */}
        {(org || position) && (
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {org && (orgHref ? (
              <span role="link" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); hardNavigate(orgHref); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); hardNavigate(orgHref); } }}
                className="cursor-pointer text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 transition-colors">🏦 {org}</span>
            ) : (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">🏦 {org}</span>
            ))}
            {position && (positionHref ? (
              <span role="link" tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); hardNavigate(positionHref); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); hardNavigate(positionHref); } }}
                className="cursor-pointer text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200 hover:bg-teal-100 hover:border-teal-400 transition-colors">💼 {position}</span>
            ) : (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200">💼 {position}</span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="text-[13px] font-bold text-teal-800 leading-snug line-clamp-2 mb-2 flex-1">{title}</h3>

        {/* Author + Rating */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-1.5 min-w-0">
            {authorAvatar ? (
              <img src={authorAvatar} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-teal-600 to-teal-400 flex items-center justify-center text-white text-[8px] font-extrabold shrink-0">
                {(isOwner ? "B" : (author || "?")[0]).toUpperCase()}
              </div>
            )}
            <span className="text-[11px] text-[#94a3b8] truncate">
              {isOwner || !authorId ? (
                <span onClick={e => { e.stopPropagation(); onAuthorClick?.(); }}
                  className="font-semibold text-teal-700 hover:text-orange-500 hover:underline cursor-pointer transition-colors">
                  {isOwner ? "Bạn" : (author || "Ẩn danh")}
                </span>
              ) : (
                <UserHoverCard
                  userId={authorUsername || authorId}
                  name={author || "Ẩn danh"}
                  className="text-[11px] text-teal-700"
                />
              )}
            </span>
          </div>
          {rating > 0 && (
            <span className="flex items-center gap-0.5 text-[10px] text-amber-500 font-semibold shrink-0">
              <Star className="w-3 h-3 fill-amber-400" />{rating}
            </span>
          )}
        </div>

        {/* Đã mua badge */}
        {isPurchased && price !== "free" && price !== 0 && (
          <div className="mb-2">
            <span className="inline-flex items-center gap-1 text-[9px] font-bold text-slate-400">
              <Gem className="w-2.5 h-2.5" />
              Đã mua · {price} 💎
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-3 pb-3 border-b border-slate-100">
          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{attempts} lượt thi</span>
          {viewCount !== undefined && viewCount > 0 && (
            <span className="flex items-center gap-1">👁 {viewCount} lượt xem</span>
          )}
        </div>

        {/* CTA */}
        <span
          className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-orange-500 text-white text-[12px] font-bold hover:bg-orange-600 transition-colors shadow-[0_2px_8px_rgba(249,115,22,.25)]">
          <Play className="w-3.5 h-3.5" />Làm bài ngay
        </span>
      </div>
    </div>
  );
}